import React from "react";

// More flexible interface to accommodate existing code
interface LeadStatusProps {
  status: string[] | any;
  preferences?: any;
  isTag?: boolean;
  showAll?: boolean;
}

const LeadStatus: React.FC<LeadStatusProps> = ({ 
  status = [], 
  preferences,
  isTag = false,
  showAll = false 
}) => {
  // Make sure status is always an array
  const statusArray = Array.isArray(status) ? status : [status].filter(Boolean);
  
  // First try to use the passed preferences, then fall back to localStorage if needed
  let preferencesStatus: any[] = [];
  
  // Use passed preferences if available
  if (preferences?.status && Array.isArray(preferences.status)) {
    preferencesStatus = preferences.status;
  }
  // Otherwise try localStorage
  else {
    try {
      const userPrefs = JSON.parse(localStorage.getItem("user_preferences") || "{}");
      if (userPrefs?.status && Array.isArray(userPrefs.status)) {
        preferencesStatus = userPrefs.status;
      }
    } catch (error) {
      console.error("Error parsing user preferences:", error);
    }
  }
  
  // Default colors for statuses with no preference match
  const defaultColors = [
    '#FF7043', '#5C6BC0', '#26A69A', '#EC407A', '#AB47BC', 
    '#42A5F5', '#FFA726', '#78909C', '#66BB6A', '#EF5350'
  ];
  
  // Process all statuses to ensure we display them all, even those not in preferences
  const processedStatuses = statusArray.map(statusValue => {
    // Try to find a matching preference
    const matchingPref = preferencesStatus.find(item => item.value === statusValue);
    
    if (matchingPref) {
      // If found in preferences, use those details
      return {
        value: statusValue,
        name: matchingPref.name,
        color: matchingPref.color
      };
    } else {
      // If not found, create a fallback display version
      // Generate a consistent color based on the status value
      const hashCode = (statusValue || '').split('').reduce(
        (acc: number, char: string) => (acc * 31 + char.charCodeAt(0)) & 0xFFFFFFFF, 0
      );
      
      return {
        value: statusValue,
        name: statusValue, // Use the value as the display name
        color: defaultColors[Math.abs(hashCode) % defaultColors.length],
        isLegacy: true // Mark as legacy/unmapped status
      };
    }
  });
  
  // Generate tooltip text with all statuses
  const tooltipText = processedStatuses.length > 1 
    ? `All statuses: ${processedStatuses.map(s => s.name).join(", ")}`
    : processedStatuses.map(s => s.name).join(", ");
  
  return (
    <div className="w-100" title={tooltipText}>
      {isTag ? (
        <>
          {processedStatuses.map((status, index) => (
            <div
              key={index}
              style={{
                backgroundColor: status?.color,
                color: "#fff",
                borderRadius: "10px",
                fontFamily: "Gilroy-Bold",
                // Add dashed border for legacy statuses
                border: status.isLegacy ? '1px dashed rgba(255,255,255,0.5)' : 'none'
              }}
              className="cell_label mt-1 p-1 px-2 fitContant"
              title={status.isLegacy ? `Unmapped status: ${status.value}` : status.name}
            >
              {status?.name}
            </div>
          ))}
        </>
      ) : (
        <div>
          {processedStatuses.map((status, index) => (
            <span key={index}>
              {index > 0 && ", "}
              {status?.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadStatus;